import { queryOptions } from '@tanstack/react-query'

import type { AnnotationBody, AnnotationEnvelope } from '@/api/annotations'
import { fetchJson, path } from '@/api/client'
import { keys } from '@/api/keys'
import type { AnnotationKind, Lint, Proposal, ProposalDiff, ProposalStatus, ProposalSummary } from '@/schema'

/**
 * `/api/proposals/*` and `/api/review/next` (§5.3, §9.4).
 *
 * A proposal is an AI or heuristic annotation body waiting for a human.
 * `GET /api/proposals` returns rows **without** `body`; `GET /api/proposals/{id}`
 * adds `body` and the server-computed `diff` against the current draft.
 */

export type { Proposal, ProposalDiff, ProposalStatus, ProposalSummary }

/** §4.5: ≥ 0.75 green (auto-accept eligible), 0.5–0.75 amber, < 0.5 grey. */
export const AUTO_ACCEPT_THRESHOLD = 0.75
export const LOW_CONFIDENCE_THRESHOLD = 0.5

export type ConfidenceBand = 'high' | 'medium' | 'low'

export function confidenceBand(confidence: number): ConfidenceBand {
  if (confidence >= AUTO_ACCEPT_THRESHOLD) return 'high'
  if (confidence >= LOW_CONFIDENCE_THRESHOLD) return 'medium'
  return 'low'
}

export interface ProposalListFilters {
  status?: ProposalStatus
  kind?: AnnotationKind
  sort?: 'confidence' | 'created'
  page?: number
  size?: number
}

export interface ProposalsPage {
  items: ProposalSummary[]
  total: number
}

export function listProposals(filters: ProposalListFilters = {}, signal?: AbortSignal): Promise<ProposalsPage> {
  return fetchJson<ProposalsPage>('/api/proposals', {
    query: { status: filters.status, kind: filters.kind, sort: filters.sort, page: filters.page, size: filters.size },
    signal,
  })
}

export function getProposal(id: number, signal?: AbortSignal): Promise<Proposal> {
  return fetchJson<Proposal>(path`/api/proposals/${id}`, { signal })
}

export interface AcceptProposalRequest {
  /** Absent = every feature above `threshold`. */
  feature_ids?: string[]
  /** Default 0.75. */
  threshold?: number
  mark_reviewed?: boolean
}

export interface AcceptProposalResponse {
  envelope: AnnotationEnvelope
  proposal: Proposal
}

export function acceptProposal(id: number, req: AcceptProposalRequest = {}): Promise<AcceptProposalResponse> {
  return fetchJson<AcceptProposalResponse>(path`/api/proposals/${id}/accept`, { method: 'POST', body: req })
}

export interface MergePreviewRequest {
  /** The unsaved on-screen document; the server head when left out. */
  base_body?: AnnotationBody
  /** Proposal feature ids to take; `threshold` is ignored when given. */
  feature_ids?: string[]
  /** Default 0.75. */
  threshold?: number
}

export interface MergePreviewResponse {
  /** The merged document, cleaned, with fresh `lints` / `affordances`. */
  body: AnnotationBody
  /** `changed` / `unchanged` / `locked_skipped` name DOCUMENT ids (a matched feature keeps its id). */
  diff: ProposalDiff
  lints: Lint[]
  affordances: string[]
  proposal: ProposalSummary & { job_id?: number | null }
  head_rev: number
  /** The PROPOSAL feature ids this merge took — what the next `PUT` reports as `accepted_ids`. */
  accepted_ids: string[]
}

/**
 * `POST /api/proposals/{id}/merge-preview` — the accept, dry: nothing is written and the
 * proposal stays pending. The editor applies `body` as ONE undoable local edit and reports the
 * proposal on its next save. `422` when the merged document would not save.
 */
export function mergePreview(id: number, req: MergePreviewRequest = {}, signal?: AbortSignal): Promise<MergePreviewResponse> {
  return fetchJson<MergePreviewResponse>(path`/api/proposals/${id}/merge-preview`, { method: 'POST', body: req, signal })
}

export function rejectProposal(id: number, featureIds?: string[]): Promise<Proposal> {
  return fetchJson<Proposal>(path`/api/proposals/${id}/reject`, { method: 'POST', body: { feature_ids: featureIds } })
}

export interface ReviewNext {
  proposal_id: number
  asset_uid: string
}

/** `GET /api/review/next` — the linear review flow; `null` when the queue is empty. */
export function reviewNext(opts: { after?: number; kind?: AnnotationKind; status?: ProposalStatus } = {}, signal?: AbortSignal): Promise<ReviewNext | null> {
  return fetchJson<ReviewNext | null>('/api/review/next', {
    query: { after: opts.after, kind: opts.kind, status: opts.status ?? 'pending' },
    signal,
  })
}

/* -------------------------------------------------------------------------- */
/* Query options                                                               */
/* -------------------------------------------------------------------------- */

export const proposalsQuery = (filters: ProposalListFilters = {}) =>
  queryOptions({
    queryKey: keys.proposals.list(filters),
    queryFn: ({ signal }) => listProposals(filters, signal),
    staleTime: 5_000,
  })

export const proposalQuery = (id: number | null) =>
  queryOptions({
    queryKey: keys.proposals.detail(id ?? 0),
    queryFn: ({ signal }) => getProposal(id!, signal),
    enabled: id !== null && id > 0,
    staleTime: 30_000,
  })

export const reviewNextQuery = (opts: { after?: number; kind?: AnnotationKind } = {}) =>
  queryOptions({
    queryKey: keys.proposals.next(opts),
    queryFn: ({ signal }) => reviewNext(opts, signal),
    staleTime: 0,
  })

/* -------------------------------------------------------------------------- */
/* Diff helpers                                                                */
/* -------------------------------------------------------------------------- */

export const EMPTY_DIFF: ProposalDiff = { added: [], changed: [], unchanged: [], removed: [], locked_skipped: [] }

export interface DiffCounts {
  added: number
  changed: number
  unchanged: number
  removed: number
  locked: number
  /** Everything a human is being asked about. */
  actionable: number
}

export function diffCounts(diff: ProposalDiff | undefined): DiffCounts {
  const d = diff ?? EMPTY_DIFF
  return {
    added: d.added.length,
    changed: d.changed.length,
    unchanged: d.unchanged.length,
    removed: d.removed.length,
    locked: d.locked_skipped.length,
    actionable: d.added.length + d.changed.length + d.removed.length,
  }
}

/** One plain-English sentence for the review bar and the queue row. */
export function describeDiff(diff: ProposalDiff | undefined): string {
  const c = diffCounts(diff)
  const bits: string[] = []
  if (c.added) bits.push(`${c.added} new ${c.added === 1 ? 'shape' : 'shapes'}`)
  if (c.changed) bits.push(`${c.changed} changed`)
  if (c.removed) bits.push(`${c.removed} gone`)
  if (bits.length === 0) return c.unchanged ? 'Nothing new — everything matches what you already have.' : 'Nothing to review.'
  const tail = c.locked ? `, ${c.locked} left alone because you locked ${c.locked === 1 ? 'it' : 'them'}` : ''
  return `${bits.join(', ')}${tail}.`
}
